import { useState } from 'react';
import { Box, Button, Heading } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { AddressForm } from '@duke-hq/google-address/components/AddressForm';
import type { AddressFormData, AddressSubmissionData } from '@duke-hq/google-address/types';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { useTenantContext } from '../../../TenantContext';
import type { EmployerDetail } from '../types';

const ADDRESS_FORM_ID = 'edit-employer-address-form';

export type AddressPayload = {
  formattedAddress: string | null;
  addressSubpremise: string | null;
  addressStreetNumber: string | null;
  addressStreetName: string | null;
  addressLocality: string | null;
  addressState: EmployerDetail['addressState'];
  addressPostcode: string | null;
};

export function AddressEditForm({
  employer,
  onSave,
  onCancel,
  isSaving,
}: {
  employer: EmployerDetail;
  onSave: (payload: AddressPayload) => void;
  onCancel: () => void;
  isSaving: boolean;
}): React.ReactElement {
  const ctx = useTenantContext();
  const googleApiKey = ctx?.apis.GOOGLE_ADDRESS_VALIDATION_API_KEY ?? '';

  const [initialData] = useState<Partial<AddressFormData>>(() => ({
    address: employer.formattedAddress ?? '',
    formattedAddress: employer.formattedAddress ?? undefined,
    addressStreetNumber: employer.addressStreetNumber ?? undefined,
    addressStreetName: employer.addressStreetName ?? undefined,
    addressStreetSubpremise: employer.addressSubpremise ?? undefined,
    addressLocality: employer.addressLocality ?? undefined,
    addressState: employer.addressState ?? undefined,
    addressPostcode: employer.addressPostcode ?? undefined,
  }));
  // Match the student address card: an existing valid address is editable and
  // saveable immediately, with the "save anyway" postcode-warning flow.
  const [isSubmittable, setIsSubmittable] = useState(true);
  const [hasPendingWarnings, setHasPendingWarnings] = useState(false);

  const handleSubmit = (address: AddressSubmissionData) => {
    onSave({
      formattedAddress: address.formattedAddress ?? null,
      addressSubpremise: address.addressStreetSubpremise ?? null,
      addressStreetNumber: address.addressStreetNumber ?? null,
      addressStreetName: address.addressStreetName ?? null,
      addressLocality: address.addressLocality ?? null,
      addressState: address.addressState ?? null,
      addressPostcode: address.addressPostcode ?? null,
    });
  };

  return (
    <FormMainContainer>
      <FormContentContainer>
        <Box w="full">
          <Heading fontSize="lg" size="xs" w="full" pb={4}>
            <Trans>Edit address</Trans>
          </Heading>
          <AddressForm
            googleApiKey={googleApiKey}
            initialData={initialData}
            onSubmit={handleSubmit}
            showPostalAddress={false}
            validationMode="strict"
            isEditMode
            showTitle={false}
            formId={ADDRESS_FORM_ID}
            onSubmittableChange={setIsSubmittable}
            onPendingWarningsChange={setHasPendingWarnings}
          />
        </Box>
      </FormContentContainer>

      <FormFooterContainer>
        <Button
          type="submit"
          form={ADDRESS_FORM_ID}
          isLoading={isSaving}
          isDisabled={!isSubmittable}
          colorScheme={hasPendingWarnings ? 'orange' : undefined}
        >
          {hasPendingWarnings ? <Trans>Save anyway</Trans> : <Trans>Save</Trans>}
        </Button>
        <Button variant="secondary" onClick={onCancel} isDisabled={isSaving}>
          <Trans>Cancel</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}
